import React, { useState, useEffect } from "react";
import { X, UserX } from "lucide-react";

/**
 * Small modal to collect a reason when archiving user(s).
 * Renders a preset radio list + optional "Other" free text.
 *
 * Props:
 * - open: bool
 * - onClose: () => void
 * - onConfirm: (reason: string) => Promise<void>
 * - title: string (e.g. "Archive Sara Cohen" or "Archive 8 selected users")
 * - subtitle: string (extra context, optional)
 */
const PRESETS = [
  { value: "graduated", label: "Graduated" },
  { value: "transferred", label: "Transferred to another school" },
  { value: "withdrew", label: "Withdrew" },
  { value: "inactive", label: "Inactive / no longer attending" },
  { value: "other", label: "Other…" },
];

export default function ArchiveReasonModal({ open, onClose, onConfirm, title, subtitle }) {
  const [reason, setReason] = useState("graduated");
  const [otherText, setOtherText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("graduated");
      setOtherText("");
      setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    const finalReason = reason === "other" ? otherText.trim() : reason;
    if (reason === "other" && !finalReason) return;
    setBusy(true);
    try {
      await onConfirm(finalReason);
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="archive-reason-modal"
      onClick={onClose}
    >
      <div className="brutal-card bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b-4 border-brand-ink">
          <div className="flex items-center gap-2">
            <UserX className="w-5 h-5" strokeWidth={3} />
            <h2 className="text-lg font-bold text-brand-navy">{title}</h2>
          </div>
          <button onClick={onClose} data-testid="archive-reason-close" className="p-1 hover:bg-brand-cream rounded">
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {subtitle && (
            <p className="text-sm text-brand-navy/70">{subtitle}</p>
          )}
          <div className="text-xs font-bold uppercase text-brand-navy/70">Reason (kept on the record)</div>
          <div className="space-y-2">
            {PRESETS.map((p) => (
              <label
                key={p.value}
                className={`flex items-center gap-3 p-2 border-2 border-brand-ink rounded-lg cursor-pointer ${reason === p.value ? "bg-brand-yellow" : "bg-white"}`}
                data-testid={`archive-reason-${p.value}`}
              >
                <input
                  type="radio"
                  name="archive-reason"
                  value={p.value}
                  checked={reason === p.value}
                  onChange={() => setReason(p.value)}
                  className="w-4 h-4"
                />
                <span className="text-sm font-bold text-brand-navy">{p.label}</span>
              </label>
            ))}
            {reason === "other" && (
              <input
                type="text"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder="Type a short reason…"
                autoFocus
                maxLength={80}
                data-testid="archive-reason-other-text"
                className="brutal-input w-full"
              />
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t-4 border-brand-ink bg-brand-cream/50">
          <button onClick={onClose} data-testid="archive-reason-cancel" className="brutal-btn-white text-sm py-2">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || (reason === "other" && !otherText.trim())}
            data-testid="archive-reason-confirm"
            className="brutal-btn-pink text-sm py-2 disabled:opacity-50"
          >
            <UserX className="w-3.5 h-3.5" strokeWidth={3} /> {busy ? "Archiving…" : "Archive"}
          </button>
        </div>
      </div>
    </div>
  );
}
